import qs from 'qs';

import Model, { skipStatusHeader } from '~/models/base/Model';

import Investigator from '~/models/Investigator';
import Artifact from '~/models/Artifact';
import Object from '~/models/Object';
import Owner from '~/models/Owner';
import Note from '~/models/Note';

/**
 * class representing an incident
 * @extends Model
 * @author mm
 */
export default class Incident extends Model {
    static _url = `${Model._url}/incidents/`;
    static _legacyUrl = `${Model._legacyUrl}/save_incident.php`;

    constructor(id, options) {
        const incidentId = id ?? null;
        super(incidentId, options);
        this.artifacts = {
            all: () => {
                return Artifact.all(incidentId);
            },
            get: artifactId => {
                return Artifact.get(incidentId, artifactId);
            }
        };
    }

    static get(id) {
        const requestUrl = `${this._url}${id}/`;
        return this.api.get(requestUrl);
    }

    static getColumns() {
        const requestUrl = `${this._url}columns/`;
        return this.api.get(requestUrl);
    }

    static getInvolvedList() {
        const requestUrl = `${this._url}user-involved/`;
        return this.api.get(requestUrl);
    }

    static getWidgetDetails(incidentId) {
        const requestUrl = `${this._url}${incidentId}/widgets/`;
        return this.api.get(requestUrl);
    }

    static getChildren(incidentId) {
        const requestUrl = `${this._url}${incidentId}/children/`;
        return this.api.get(requestUrl);
    }

    static getInsightURL(incidentId) {
        const requestUrl = `${this._url}${incidentId}/insight_url/`;
        return this.api.get(requestUrl);
    }

    static getPermissions(incidentId) {
        const requestUrl = `${this._url}${incidentId}/permissions/`;
        return this.api.get(requestUrl);
    }

    /**
     * Retrieve labels for incidentID field
     * @return {AxiosPromise<any>}
     */
    static getLabelIncidentID() {
        const requestUrl = `${this._url}labels/`;
        return this.api.get(requestUrl);
    }

    static getFields(incidentId) {
        const requestUrl = `${this._url}${
            incidentId ? `${incidentId}/` : ''
        }fields/`;
        return this.api.get(requestUrl);
    }

    static getIncidentOverview(id) {
        return this.getFields(id);
    }

    static searchIncident(params) {
        return this.api.get(`${this._url}search/?keyword=${params?.keyword}`);
    }

    static fetchRelatedIncidentsBaseInfo = incidentIds => {
        const requestUrl = `${APP_URL.api}/incidents/baseInfo/`;
        const requestPayload = { ids: incidentIds.join(',') };
        return this.api.post(requestUrl, requestPayload);
    };

    static favorite(params) {
        const requestUrl = `${this._url}favorite/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Clone incident
     * @param incidentId
     * @param params (incident_id, number_of_clones, auto_label_id, incidentid)
     * @return {AxiosPromise<any>}
     */
    static clone(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/clone/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Delete incident and his children
     * @param incidentId
     * @param params
     * @return {AxiosPromise<any>}
     */
    static delete(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/delete/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    static bulkDeleteIncidents(params) {
        const requestUrl = `${this._url}bulk/delete/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    static bulkPhysicalDeleteIncidents(params) {
        const requestUrl = `${this._url}bulk/physicaldelete/`;
        const requestPayload = { data: params ?? {} };
        return this.api.delete(requestUrl, requestPayload);
    }

    static isDeleted(incidentId) {
        const requestUrl = `${this._url}${incidentId}/isdeleted/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Restore delete incident and his children
     * @param incidentId
     * @param params
     * @return {AxiosPromise<any>}
     */
    static restore(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/restore/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    static bulkRestoreIncidents(params) {
        const requestUrl = `${this._url}bulk/restore/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Method to get data for close incident and for put status to closed
     * @return {AxiosPromise<any>}
     * @author FM
     */
    static close(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/close/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    static bulkCloseIncidents(params) {
        const requestUrl = `${this._url}bulk/close/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Reopen specific incident
     * @param {(integer|string)} [incidentId] - Id of the incident to reopen
     * @param {Object} [data] - ex:  {op_type: 'reopen', propagateToChildren: 1, forcePropagation: 0}
     * @returns {AxiosPromise<any>}
     *
     * @author mm
     */
    static reopen(incidentId, data) {
        const requestUrl = `${this._url}${incidentId}/reopen/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = data;
        return this.api.put(requestUrl, requestPayload);
    }

    static bulkReOpenIncidents(params) {
        const requestUrl = `${this._url}bulk/reopen/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static save(params) {
        const requestUrl = this._url;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static bulkEditSave(params) {
        const requestUrl = `${this._url}bulk/edit/`;
        const requestPayload = params ?? {};
        return this.api.put(requestUrl, requestPayload);
    }

    /**
     * Save/update fields on incident
     * @param incidentId - ID of incident
     * @param fieldId - Field id (es opt_2)
     * @param fieldValue - Value of the right field
     * @return {AxiosPromise<any>}
     * @author FM
     */
    static saveFieldForIncident(incidentId, fieldId, fieldValue) {
        const requestUrl = `${this._url}${incidentId}/`;
        const requestPayload = { field_id: fieldId, field_value: fieldValue };
        return this.api.patch(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    getNotes() {
        return Note.all({
            incidentId: this.id
        });
    }

    static updateNote(incidentId, noteId, data) {
        const requestUrl = `${this._url}${incidentId}/note/${noteId}/`;
        return this.api.put(requestUrl, data);
    }

    static createNote(incidentId, data) {
        const requestUrl = `${this._url}${incidentId}/notes/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = data;
        return this.api.post(requestUrl, requestPayload);
    }

    static updateNotesOrder(incidentId, data) {
        const requestUrl = `${this._url}${incidentId}/notes/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = data;
        return this.api.patch(requestUrl, requestPayload);
    }

    static deleteNote(incidentId, noteIds, isBulk) {
        const requestUrl = `${this._url}${incidentId}/notes/delete/`;
        const requestPayload = { is_bulk: isBulk, notes: noteIds };
        return this.api.post(requestUrl, requestPayload);
    }

    static restoreNote(incidentId, noteIds, isBulk) {
        const requestUrl = `${this._url}${incidentId}/notes/restore/`;
        const requestPayload = { is_bulk: isBulk, notes: noteIds };
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static execTrigger(incidentId, data) {
        const requestUrl = `${APP_URL.automation}/executeTrigger/incident/${incidentId}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = data;
        return this.api.post(requestUrl, requestPayload);
    }

    static getCustomTrigger(incidentId) {
        return this.api.get(`${this._url}${incidentId}/hooks/`);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static exportRunbook(runbookId, incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/runbook/${runbookId}/export-internally/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static deleteRunbook(incidentId, runbookId) {
        const requestUrl = `${this._url}${incidentId}/runbook/${runbookId}/`;
        return this.api.delete(requestUrl);
    }

    static associateRunbooks(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/runbook/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static getAssociateRunbooks(incidentId) {
        const requestUrl = `${this._url}${incidentId}/runbook/`;
        return this.api.get(requestUrl);
    }

    static getAvailableRunbooks(incidentId) {
        const requestUrl = `${this._url}${incidentId}/runbook/available/`;
        return this.api.get(requestUrl);
    }

    static getRunbookList(incidentId) {
        const requestUrl = `${this._url}${incidentId}/runbook/list/`;
        return this.api.get(requestUrl);
    }

    static getRunbookListHistory(incidentId) {
        const requestUrl = `${this._url}${incidentId}/runbook/list/history/`;
        return this.api.get(requestUrl);
    }

    static getRunbookListResults(incidentId) {
        const requestUrl = `${this._url}${incidentId}/runbook/list/results/`;
        return this.api.get(requestUrl);
    }

    static getRunbookListExecutionResults(incidentId, runningId) {
        const requestUrl = `${this._url}${incidentId}/runbook/list/results/?running_id=${runningId}`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static updateTasksOrder(incidentId, data) {
        const requestUrl = `${this._url}${incidentId}/tasks/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = data;
        return this.api.patch(requestUrl, requestPayload);
    }

    static getTasks(incidentId, ws_invoke = false) {
        const requestUrl = `${this._url}${incidentId}/tasks/`;
        const requestPayload = {};
        const requestConfig = {};
        if (ws_invoke) {
            requestConfig.headers = { ...skipStatusHeader };
        }
        return this.api.get(requestUrl, requestPayload, requestConfig);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    getObjects() {
        return Object.all({
            incidentId: this.id
        });
    }

    static objectBulkDelete(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/objects/`;
        const requestPayload = { data: params };
        return this.api.delete(requestUrl, requestPayload);
    }

    static objectBulkRestore(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/objectsrestore/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getAvailableInvestigators(id) {
        return Investigator.all({
            id: id
        });
    }

    //rn: TODO check if is possible to use this api instead of other investigator legacy call
    static getInvestigators(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/investigator/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static bulkAddInvestigator(params) {
        const requestUrl = `${this._url}bulk/investigators/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static addInvestigators(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/investigators/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static editInvestigatorRole(incidentId, investigatorId, params) {
        const requestUrl = `${this._url}${incidentId}/investigators/${investigatorId}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    static removeInvestigator(incidentId, investigatorId, params) {
        const requestUrl = `${this._url}${incidentId}/investigators/${investigatorId}/delete/`;
        const requestPayload = { data: params }; // <<
        return this.api.delete(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Add Related incidents
     * @param incidentId
     * @param params (related_incidents)
     * @return {AxiosPromise<any>}
     */
    static addRelated(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/related/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Remove Related incident
     * @param incidentId
     * @param params (related_incidents)
     * @return {AxiosPromise<any>}
     */
    static removeRelated(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/related/delete/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Retrieve Related incidents of incident
     * @param incidentId
     * @param params
     * @return {AxiosPromise<any>}
     */
    static getRelated(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/related/check/?query=${params?.query}`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Chat:
     * Retrieve the chat of an incident (returns a channel ID)
     */
    static retrieveIncidentChannel(incidentId) {
        const requestUrl = `${this._url}${incidentId}/botChannel/`;
        return this.api.get(requestUrl);
    }

    /**
     * Chat:
     * Create the chat channel of an incident (returns a channel ID)
     */
    static createIncidentChannel(incidentId) {
        const requestUrl = `${this._url}${incidentId}/botChannel/`;
        return this.api.post(requestUrl);
    }

    static retrieveChannelHistory(incidentId, channelId) {
        const requestUrl = `${this._url}${incidentId}/botChannelHistory/${channelId}/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static searchAttachment(id, params) {
        const requestUrl = `${this._url}${id}/attachments/search/?kw=${params?.keyword}`;
        return this.api.get(requestUrl);
    }

    static deleteAttachment(incidentId, attachmentId) {
        const requestUrl = `${this._url}${incidentId}/attachments/${attachmentId}/delete/`;
        return this.api.patch(requestUrl);
    }

    static restoreAttachment(incidentId, attachmentId) {
        const requestUrl = `${this._url}${incidentId}/attachments/${attachmentId}/restore/`;
        return this.api.patch(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static downloadEmailAttachment(incidentId, emailId, attachmentId) {
        const requestUrl = `${this._url}${incidentId}/email/${emailId}/download/${attachmentId}/`;
        const requestPayload = { responseType: 'blob' };
        return this.api.get(requestUrl, requestPayload);
    }

    static removeEmail(incidentId, emailId) {
        const requestUrl = `${this._url}${incidentId}/email/${emailId}/remove/`;
        return this.api.put(requestUrl);
    }

    static removeExternalAlertEvent(incidentId, eventId, type) {
        if ('email' === type) {
            return this.removeEmail(incidentId, eventId);
        }
    }

    static getExternalAlertEmailDetails(incidentId, emailId) {
        const requestUrl = `${this._url}${incidentId}/email/${emailId}/`;
        return this.api.get(requestUrl);
    }

    static getExternalAlertDaemonDetails(incidentId, daemonId) {
        const requestUrl = `${this._url}${incidentId}/daemon/${daemonId}/`;
        return this.api.get(requestUrl);
    }

    static getIncidentExternalAlertDetails(incidentId, alertId) {
        const requestUrl = `${this._url}${incidentId}/alert/${alertId}/`;
        return this.api.get(requestUrl);
    }

    static getExternalAlertDetails(incidentId, eventId, type) {
        //prettier-ignore
        switch (type) {
            case 'email':
                return this.getExternalAlertEmailDetails(incidentId, eventId);
            case 'daemon':
                return this.getExternalAlertDaemonDetails(incidentId, eventId);
            case 'external_alert':
                return this.getIncidentExternalAlertDetails(incidentId, eventId);
        }
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static exportCsv(params) {
        const requestUrl = `${this._url}search/export/csv/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    static getCsv(exportId) {
        const requestUrl = `${this._url}search/export/csv/download/${exportId}/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static createFolder(params) {
        const requestUrl = `${super._url}/folders/`;
        const requestPayload = params ?? {};
        return this.api.post(requestUrl, requestPayload);
    }

    static changeFolder(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/folder/`;
        const requestPayload = params ?? {};
        return this.api.patch(requestUrl, requestPayload);
    }

    static bulkChangeFolder(params) {
        const requestUrl = `${this._url}bulk/folder/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.patch(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static warRoomData(incidentId, eventTypesToRetrieve) {
        const requestUrl = `${this._url}${incidentId}/war-room/?sections=${eventTypesToRetrieve}`;
        return this.api.get(requestUrl);
    }

    static warRoomExport(incidentId, documentType, params) {
        const requestUrl = `${this._url}${incidentId}/war-room/export/${documentType}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    static getWarRoomExportDocument(incidentId, documentType, fileId) {
        const requestUrl = `${this._url}${incidentId}/war-room/export/${documentType}/?download=${fileId}`;
        const requestPayload = { responseType: 'blob' };
        return this.api.get(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getPhases(incidentId) {
        const requestUrl = `${this._url}${incidentId}/phases/`;
        return this.api.get(requestUrl);
    }

    /**
     * @param {Object} params - accept: incident_id, phase_id, incident_status, phase_time
     * @return {AxiosPromise<any>}
     */
    static changePhase(params) {
        const requestPayload = params || {};
        const requestUrl = `${this._url}${requestPayload.incident_id}/phases/`;
        const ms = requestPayload.phase_time;
        requestPayload.phase_time = Date.parse(ms) / 1e3;
        return this.api.post(requestUrl, requestPayload);
    }

    static checkMissingMandatoryPhases(params) {
        const requestPayload = params || {};
        requestPayload.op_type = 'checkMissingMandatoryPhases';
        return this.legacyCall(requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getAvailableOwners(id) {
        return Owner.all({
            incident: id
        });
    }

    static changeOwnership(incidentId, params) {
        const requestUrl = `${this._url}${incidentId}/owner/`;
        const requestPayload = params ?? {};
        return this.api.patch(requestUrl, requestPayload);
    }

    static bulkChangeOwnership(params) {
        const requestUrl = `${this._url}bulk/owner/`;
        const requestPayload = params ?? {};
        return this.api.patch(requestUrl, requestPayload);
    }

    static getHistoryOwner(incidentId) {
        const requestUrl = `${this._url}${incidentId}/ownership/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getHistoryParent(incidentId) {
        const requestUrl = `${this._url}${incidentId}/parenthistory/`;
        return this.api.get(requestUrl);
    }

    static updateParent(params) {
        params = params || {};
        const requestUrl = `${this._url}${params.id}/parent/`;
        const requestPayload = qs.stringify({ parent_id: params.parent_id });
        return this.api.patch(requestUrl, requestPayload);
    }

    /**
     * Retrieve a list of possible incident parents
     * @param {object} [params = { id_to_exclude: 123 }] - The id of an incident to exclude from response
     * @returns {AxiosPromise<any>}
     * @author rn
     */
    static getAvailableParents(params) {
        const requestUrl = `${this._url}parents-available/`;
        const requestPayload = { params: params ?? {} };
        return this.api.get(requestUrl, requestPayload);
    }

    static checkIncidentTestPlaybook(payload = {}) {
        const requestUrl = `${this._url}check-incident-test-playbook/`;
        return this.api.post(requestUrl, payload);
    }
}
